import type {
  Collaborator,
  CollaboratorStatus,
} from '@components/ArtBoard/Collaboration/types'
import type { AppState } from '@store/types'
import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import type { AuthState } from '@/types'
import { useSocketListeners } from './useSocketListeners'

export const useCollaboratorsState = (): Collaborator[] => {
  const auth = useSelector<AppState, AuthState>((state: AppState) => state.auth)
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const currentSocketId = useRef<string | null>(null)

  const updateSingleCollaborator = useCallback((collaborator: Collaborator) => {
    setCollaborators((prevCollaborators) => {
      const existingIndex = prevCollaborators.findIndex(
        (c) => c.id === collaborator.id
      )

      if (existingIndex !== -1) {
        const updatedCollaborators = [...prevCollaborators]
        updatedCollaborators[existingIndex] = {
          ...updatedCollaborators[existingIndex],
          offset: collaborator.offset,
          status: collaborator.status,
        }

        return updatedCollaborators
      }

      return prevCollaborators
    })
  }, [])

  const updateAllCollaborators = useCallback(
    (newCollaborators: Collaborator[]) => {
      setCollaborators(newCollaborators)
    },
    []
  )

  useSocketListeners(
    currentSocketId,
    updateAllCollaborators,
    updateSingleCollaborator
  )

  if (collaborators.length === 1 && collaborators[0].userId === auth.id) {
    return []
  }

  return collaborators
    .map((collaborator) => {
      if (
        collaborator.id === currentSocketId.current ||
        collaborator.userId === auth.id
      ) {
        return { ...collaborator, status: 'idle' as CollaboratorStatus }
      }
      return collaborator
    })
    .sort((a, b) => {
      if (a.id === currentSocketId.current) {
        return -1
      }
      if (b.id === currentSocketId.current) {
        return 1
      }
      return 0
    })
}
